// Static FlowViewer per PRD §10.1: parse the flow YAML, lay out via dagre,
// render with @xyflow/react. Click a node to open the side panel.
//
// Live overlay (step-by-step progress animation on the graph) lands with #20
// — V1 here is intentionally static.

import { Background, Controls, ReactFlow } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useMemo, useState } from 'react'
import { layoutFlow } from '../../lib/flow-layout.js'
import { parseFlowYaml } from '../../lib/flow-parse.js'
import { FlowNode } from './FlowNode.jsx'
import { FlowSidePanel } from './FlowSidePanel.jsx'

const nodeTypes = { flow: FlowNode }

export function FlowViewer({ yaml }) {
  const [selectedId, setSelectedId] = useState(null)

  const { nodes, edges, error } = useMemo(() => {
    try {
      const parsed = parseFlowYaml(yaml)
      const laid = layoutFlow(parsed.nodes, parsed.edges)
      return {
        nodes: laid.nodes.map((n) => ({ ...n, type: 'flow' })),
        edges: laid.edges.map((e) => ({
          ...e,
          type: 'smoothstep',
          style: { stroke: 'oklch(60% 0.008 60)', strokeWidth: 1.5 },
          labelStyle: { fill: 'oklch(30% 0.008 60)', fontSize: 11 },
        })),
        error: null,
      }
    } catch (err) {
      return { nodes: [], edges: [], error: err.message }
    }
  }, [yaml])

  const selectedNode = selectedId ? nodes.find((n) => n.id === selectedId) : null

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-sm text-[var(--color-failed)]" role="alert">
          {error}
        </p>
      </div>
    )
  }

  if (nodes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <p className="text-sm text-[var(--color-schist)]">
          This flow has no nodes yet. Add `spec.nodes` to the YAML to see the graph.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full">
      <div className="flex-1 bg-[var(--color-limestone)]">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          onNodeClick={(_, node) => setSelectedId(node.id)}
          onPaneClick={() => setSelectedId(null)}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="oklch(92% 0.008 60)" gap={16} />
          <Controls
            showInteractive={false}
            className="!border-[var(--color-sandstone)] !bg-[var(--color-limestone)]"
          />
        </ReactFlow>
      </div>
      {selectedNode && <FlowSidePanel node={selectedNode} onClose={() => setSelectedId(null)} />}
    </div>
  )
}
